/**
 * foldAllocation — fold the allocation agent's `allocation_commands` blocks
 * across a session's messages into the CURRENT proposal per patient
 * (latest-writer-wins), mirroring foldRoster's grain. The board renders these
 * as ghost suggestions; custody truth comes from the ward allocation read,
 * never from the fold.
 */

import type { MessageResponse } from '@/api/types_session';

export const ALLOCATION_COMMANDS_BLOCK_TYPE = 'allocation_commands';

export interface AllocationCommand {
  patient_id: string;
  patient?: string;
  nurse: string;
  nurse_id?: string;
}

/** Runtime narrowing for the wire block payload. */
export function isAllocationCommands(data: unknown): data is { commands: AllocationCommand[] } {
  if (typeof data !== 'object' || data === null) return false;
  const cmds = (data as { commands?: unknown }).commands;
  return (
    Array.isArray(cmds) &&
    cmds.every(
      (c) =>
        typeof c === 'object' &&
        c !== null &&
        typeof (c as AllocationCommand).patient_id === 'string' &&
        typeof (c as AllocationCommand).nurse === 'string',
    )
  );
}

/**
 * Latest proposal per patient across the whole session — EMPTY from the
 * session cut-over on: the allocation_commands block kind has no counterpart
 * in the conversation payload upstream (text / tool / citation blocks), so
 * there is nothing to fold until the allocation commands get a contract
 * (fabric directives are the candidate). The board's custody truth was always
 * the ward allocation read, which is unaffected.
 */
export function foldAllocation(_messages: MessageResponse[] | undefined): AllocationCommand[] {
  return []
}
